import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { create } from 'zustand';
import {
  AiConversation,
  AiConversationMetadata,
  AiConversationSummary,
  AiScopeTarget,
  getConversationSummaryFromConversation,
} from '@shared/models/ai-chat.model';
import type { OpenAiAllowedModels } from '@shared/models/ai/ai-models.model';
import { aiChatApi } from './ai-chat.api';
import { syncOpenProjectFromAiConversation } from './ai-project-sync.helpers';

// ---------------------------------------------------------------------------
// Per-(organization, project) chat state
//
// Conversations are bucketed by `(organizationId, projectId)`. `projectId === null`
// is the organization-level bucket (user not inside any project). Each bucket
// owns its own live chat slice + history list; per-turn scope is independent
// and lives on each {@link AiMessage}.
// ---------------------------------------------------------------------------

export type AiOrgIdle = { status: 'idle' };
export type AiOrgFetching = { status: 'fetching' };
export type AiOrgError = { status: 'error'; error: Error };
export type AiOrgReady = {
  status: 'ready';
  conversation: AiConversation;
  isFetching: boolean;
  isSending: boolean;
  sendError: Error | null;
};

export type AiOrgState = AiOrgIdle | AiOrgFetching | AiOrgError | AiOrgReady;

/** Conversation history list for a (org, project) bucket. */
export type AiConversationListForOrg = {
  data: AiConversationSummary[];
  loading: boolean;
  error: Error | null;
};

const IDLE: AiOrgIdle = { status: 'idle' };

/** Compound bucket key — `${organization}::${projectId ?? 'null'}`. */
export const bucketKey = (
  organization: string,
  projectId: number | null,
): string => `${organization}::${projectId ?? 'null'}`;

const conversationBucketKey = (
  c: Pick<AiConversation, 'organizationId' | 'projectId'>,
): string => bucketKey(c.organizationId, c.projectId);

/**
 * Subscribe to the chat slice for a (org, project) bucket and auto-fetch when idle.
 */
export const useOrgChatState = (
  organization: string,
  projectId: number | null,
): AiOrgState => {
  const key = bucketKey(organization, projectId);
  const state = useAiChatStore((s) => s.byKey[key] ?? IDLE);

  useEffect(() => {
    const cur = useAiChatStore.getState().byKey[key];
    if (cur && cur.status !== 'idle') return;
    void useAiChatStore.getState().fetchFor(organization, projectId);
  }, [key, organization, projectId]);

  return state;
};

// ---------------------------------------------------------------------------
// In-flight fetch dedupe
// ---------------------------------------------------------------------------

const inflightFetches = new Map<string, Promise<AiConversation>>();

/** Conversation IDs explicitly deleted this session. Prevents stale socket/fetch data from repopulating cleared chats. */
const deletedConversationIds = new Set<string>();

const fetchDeduped = (
  organization: string,
  projectId: number | null,
): Promise<AiConversation> => {
  const key = bucketKey(organization, projectId);
  const existing = inflightFetches.get(key);
  if (existing) return existing;
  const p = aiChatApi
    .getCurrentConversation(projectId)
    .finally(() => inflightFetches.delete(key));
  inflightFetches.set(key, p);
  return p;
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

type AiChatState = {
  byKey: Record<string, AiOrgState>;
  /** History list per (org, project) bucket; socket merges keep order by updatedAt. */
  conversationListByKey: Record<string, AiConversationListForOrg | undefined>;

  upsertConversation: (conversation: AiConversation) => void;
  setConversation: (
    organization: string,
    projectId: number | null,
    conversation: AiConversation,
  ) => void;
  fetchFor: (organization: string, projectId: number | null) => Promise<void>;
  sendMessage: (
    organization: string,
    projectId: number | null,
    text: string,
    scopeTarget: AiScopeTarget,
    model?: OpenAiAllowedModels,
    files?: File[],
  ) => Promise<void>;
  createNewConversation: (
    organization: string,
    projectId: number | null,
  ) => Promise<void>;
  deleteConversation: (
    organization: string,
    projectId: number | null,
  ) => Promise<void>;
  cancelPendingMessages: (
    organization: string,
    projectId: number | null,
    messageId?: string,
  ) => Promise<void>;
  applyConversationMetadata: (meta: AiConversationMetadata) => void;
  clearConversationListCache: () => void;
  fetchConversationList: (
    organization: string,
    projectId: number | null,
  ) => Promise<void>;
};

const put = (
  states: Record<string, AiOrgState>,
  key: string,
  value: AiOrgState,
): Record<string, AiOrgState> => ({ ...states, [key]: value });

const emptyConversation = (
  organizationId: string,
  projectId: number | null,
): AiConversation => ({
  id: '',
  organizationId,
  userId: '',
  projectId,
  scopeTarget: { scope: 'organization' },
  messages: [],
  status: 'idle',
  title: null,
  createdAt: '',
  updatedAt: '',
});

const toError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(String(e));

/** True if `incoming` is at least as recent as `current` (by {@link AiConversation.updatedAt}). */
const isNewerOrEqual = (
  incoming: AiConversation,
  current: AiConversation,
): boolean => !current.updatedAt || incoming.updatedAt >= current.updatedAt;

const mergeHistoryRow = (
  prev: AiConversationSummary[] | undefined,
  row: AiConversationSummary,
): AiConversationSummary[] => {
  const list = prev ?? [];
  const i = list.findIndex((x) => x.id === row.id);
  const next =
    i === -1 ? [...list, row] : list.map((x, j) => (j === i ? row : x));
  return [...next].sort((a, b) =>
    a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0,
  );
};

const removeHistoryRow = (
  prev: AiConversationSummary[] | undefined,
  id: string,
): AiConversationSummary[] | undefined => {
  if (!prev?.length) return prev;
  const next = prev.filter((x) => x.id !== id);
  return next.length === prev.length ? prev : next;
};

const mergeHistoryIntoBucket = (
  entry: AiConversationListForOrg | undefined,
  row: AiConversationSummary,
): AiConversationListForOrg => ({
  data: mergeHistoryRow(entry?.data, row),
  loading: entry?.loading ?? false,
  error: entry?.error ?? null,
});

const adoptOpenProjectFromAiConversation = (
  conversation: AiConversation,
): void => {
  void syncOpenProjectFromAiConversation(conversation).catch((error) => {
    Sentry.captureException(error);
  });
};

export const useAiChatStore = create<AiChatState>((set, get) => ({
  byKey: {},
  conversationListByKey: {},

  upsertConversation: (conversation) => {
    if (conversation.id && deletedConversationIds.has(conversation.id)) return;

    const key = conversationBucketKey(conversation);
    const summary = conversation.id
      ? getConversationSummaryFromConversation(conversation)
      : null;
    set(({ byKey, conversationListByKey }) => {
      const cur = byKey[key];

      if (cur?.status === 'ready') {
        const activeId = cur.conversation.id;
        const incomingId = conversation.id;
        const otherThreadUpdated = Boolean(
          activeId && incomingId && activeId !== incomingId,
        );

        const historyPatch = summary
          ? {
              conversationListByKey: {
                ...conversationListByKey,
                [key]: mergeHistoryIntoBucket(
                  conversationListByKey[key],
                  summary,
                ),
              },
            }
          : {};

        if (otherThreadUpdated) {
          return Object.keys(historyPatch).length
            ? { byKey, ...historyPatch }
            : { byKey };
        }

        if (!isNewerOrEqual(conversation, cur.conversation)) return { byKey };
        return {
          byKey: put(byKey, key, { ...cur, conversation }),
          ...historyPatch,
        };
      }

      const next: AiOrgReady = {
        status: 'ready',
        conversation,
        isFetching: cur?.status === 'fetching',
        isSending: false,
        sendError: null,
      };
      return {
        byKey: put(byKey, key, next),
        ...(summary
          ? {
              conversationListByKey: {
                ...conversationListByKey,
                [key]: mergeHistoryIntoBucket(
                  conversationListByKey[key],
                  summary,
                ),
              },
            }
          : {}),
      };
    });
    adoptOpenProjectFromAiConversation(conversation);
  },

  setConversation: (organization, projectId, conversation) => {
    const key = bucketKey(organization, projectId);
    set(({ byKey, conversationListByKey }) => {
      const cur = byKey[key];
      const next: AiOrgReady = {
        status: 'ready',
        conversation,
        isFetching: false,
        isSending: cur?.status === 'ready' ? cur.isSending : false,
        sendError: cur?.status === 'ready' ? cur.sendError : null,
      };
      if (!conversation.id) {
        return { byKey: put(byKey, key, next) };
      }
      const summary = getConversationSummaryFromConversation(conversation);
      return {
        byKey: put(byKey, key, next),
        conversationListByKey: {
          ...conversationListByKey,
          [key]: mergeHistoryIntoBucket(conversationListByKey[key], summary),
        },
      };
    });
    adoptOpenProjectFromAiConversation(conversation);
  },

  fetchFor: async (organization, projectId) => {
    const key = bucketKey(organization, projectId);
    set(({ byKey }) => {
      const cur = byKey[key];
      const next: AiOrgState =
        cur?.status === 'ready'
          ? { ...cur, isFetching: true }
          : { status: 'fetching' };
      return { byKey: put(byKey, key, next) };
    });
    try {
      const conversation = await fetchDeduped(organization, projectId);
      const tombstoned =
        conversation.id && deletedConversationIds.has(conversation.id);

      set(({ byKey }) => {
        const cur = byKey[key];

        if (tombstoned) {
          if (cur?.status === 'ready') {
            return {
              byKey: put(byKey, key, {
                ...cur,
                isFetching: false,
              }),
            };
          }
          const empty = emptyConversation(organization, projectId);
          return {
            byKey: put(byKey, key, {
              status: 'ready',
              conversation: empty,
              isFetching: false,
              isSending: false,
              sendError: null,
            }),
          };
        }

        const next: AiOrgReady =
          cur?.status === 'ready'
            ? {
                ...cur,
                conversation: isNewerOrEqual(conversation, cur.conversation)
                  ? conversation
                  : cur.conversation,
                isFetching: false,
              }
            : {
                status: 'ready',
                conversation,
                isFetching: false,
                isSending: false,
                sendError: null,
              };
        return { byKey: put(byKey, key, next) };
      });
      if (!tombstoned) {
        const applied = get().byKey[key];
        if (applied?.status === 'ready') {
          adoptOpenProjectFromAiConversation(applied.conversation);
        }
      }
    } catch (e) {
      set(({ byKey }) => {
        const cur = byKey[key];
        const next: AiOrgState =
          cur?.status === 'ready'
            ? { ...cur, isFetching: false }
            : { status: 'error', error: toError(e) };
        return { byKey: put(byKey, key, next) };
      });
    }
  },

  sendMessage: async (
    organization,
    projectId,
    text,
    scopeTarget,
    model,
    files,
  ) => {
    const key = bucketKey(organization, projectId);
    const cur = get().byKey[key];
    if (cur?.status !== 'ready') return;

    const convId = cur.conversation.id || undefined;

    set(({ byKey }) => {
      const c = byKey[key];
      if (c?.status !== 'ready') return { byKey };
      return {
        byKey: put(byKey, key, {
          ...c,
          isSending: true,
          sendError: null,
        }),
      };
    });
    try {
      const conv = await aiChatApi.sendMessage({
        scopeTarget,
        text,
        conversationId: convId,
        projectId,
        model,
        files,
      });
      set(({ byKey, conversationListByKey }) => {
        const c = byKey[key];
        if (c?.status !== 'ready') return { byKey, conversationListByKey };
        const useApiResponse = isNewerOrEqual(conv, c.conversation);
        const conversation = useApiResponse
          ? conv
          : { ...c.conversation, id: conv.id };
        const summary = getConversationSummaryFromConversation(conversation);
        return {
          byKey: put(byKey, key, {
            ...c,
            conversation,
            isSending: false,
          }),
          conversationListByKey: {
            ...conversationListByKey,
            [key]: mergeHistoryIntoBucket(conversationListByKey[key], summary),
          },
        };
      });
      // No adopt here: HTTP returns before runPipeline stamps projectUpdatedAt.
      // upsertConversation / fetchFor adopt once the processed turn arrives.
    } catch (e) {
      set(({ byKey }) => {
        const c = byKey[key];
        if (c?.status !== 'ready') return { byKey };
        return {
          byKey: put(byKey, key, {
            ...c,
            isSending: false,
            sendError: toError(e),
          }),
        };
      });
    }
  },

  createNewConversation: async (organization, projectId) => {
    const key = bucketKey(organization, projectId);
    const conv = await aiChatApi.createConversation(projectId);
    const summary = getConversationSummaryFromConversation(conv);
    set(({ byKey, conversationListByKey }) => {
      const c = byKey[key];
      const next: AiOrgReady =
        c?.status === 'ready'
          ? { ...c, conversation: conv, sendError: null }
          : {
              status: 'ready',
              conversation: conv,
              isFetching: false,
              isSending: false,
              sendError: null,
            };
      return {
        byKey: put(byKey, key, next),
        conversationListByKey: {
          ...conversationListByKey,
          [key]: mergeHistoryIntoBucket(conversationListByKey[key], summary),
        },
      };
    });
  },

  deleteConversation: async (organization, projectId) => {
    const key = bucketKey(organization, projectId);
    const cur = get().byKey[key];
    if (cur?.status !== 'ready' || !cur.conversation.id) return;
    const deletedId = cur.conversation.id;
    await aiChatApi.deleteConversation(deletedId);
    deletedConversationIds.add(deletedId);
    set(({ byKey, conversationListByKey }) => {
      const c = byKey[key];
      if (c?.status !== 'ready') return { byKey, conversationListByKey };
      const prevEntry = conversationListByKey[key];
      const prevData = prevEntry?.data;
      const nextData = removeHistoryRow(prevData, deletedId);
      const listPatch =
        nextData !== prevData && prevEntry
          ? { [key]: { ...prevEntry, data: nextData ?? [] } }
          : {};
      return {
        byKey: put(byKey, key, {
          ...c,
          conversation: emptyConversation(
            c.conversation.organizationId,
            c.conversation.projectId,
          ),
        }),
        conversationListByKey: {
          ...conversationListByKey,
          ...listPatch,
        },
      };
    });
  },

  cancelPendingMessages: async (organization, projectId, messageId) => {
    const key = bucketKey(organization, projectId);
    const cur = get().byKey[key];
    if (cur?.status !== 'ready' || !cur.conversation.id) return;
    await aiChatApi.cancelPendingMessages(cur.conversation.id, messageId);
  },

  applyConversationMetadata: (meta) => {
    const row: AiConversationSummary = {
      id: meta.id,
      preview: meta.preview,
      createdAt: meta.createdAt,
      updatedAt: meta.updatedAt,
    };
    set(({ byKey, conversationListByKey }) => {
      const key = bucketKey(meta.organizationId, meta.projectId);
      const nextEntry = mergeHistoryIntoBucket(conversationListByKey[key], row);
      const cur = byKey[key];
      if (cur?.status !== 'ready' || cur.conversation.id !== meta.id) {
        return {
          conversationListByKey: {
            ...conversationListByKey,
            [key]: nextEntry,
          },
        };
      }
      return {
        conversationListByKey: {
          ...conversationListByKey,
          [key]: nextEntry,
        },
        byKey: put(byKey, key, {
          ...cur,
          conversation: {
            ...cur.conversation,
            title: meta.title,
            status: meta.status,
            providerContext: meta.providerContext,
            createdAt: meta.createdAt,
            updatedAt: meta.updatedAt,
          },
        }),
      };
    });
  },

  clearConversationListCache: () => {
    set({ conversationListByKey: {} });
  },

  fetchConversationList: async (organization, projectId) => {
    const key = bucketKey(organization, projectId);
    set(({ conversationListByKey }) => {
      const prev = conversationListByKey[key];
      return {
        conversationListByKey: {
          ...conversationListByKey,
          [key]: {
            data: prev?.data ?? [],
            loading: true,
            error: null,
          },
        },
      };
    });
    try {
      const items = await aiChatApi.listConversations(projectId);
      set(({ conversationListByKey }) => ({
        conversationListByKey: {
          ...conversationListByKey,
          [key]: {
            data: items,
            loading: false,
            error: null,
          },
        },
      }));
    } catch (e) {
      set(({ conversationListByKey }) => {
        const prev = conversationListByKey[key];
        return {
          conversationListByKey: {
            ...conversationListByKey,
            [key]: {
              data: prev?.data ?? [],
              loading: false,
              error: toError(e),
            },
          },
        };
      });
    }
  },
}));
